import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Columns3, Download, Inbox, Loader2, RefreshCw, Rows3, Search, X,
} from 'lucide-react';
import { formatDate, parseStoredDate, toISODate } from '../utils/format';
import { csvText } from '../utils/csv';

/*
 * DataTable — the one table every list screen uses.
 *
 * It talks to list endpoints that follow backend/listing.py:
 *   GET endpoint?page&page_size&q&sort&order&<filters>  -> {items,total,page,pages,page_size}
 *   GET endpoint?export=xlsx|csv&columns=[{key,label}]  -> file with every matching row
 *
 * Features: server-side paging, debounced search, sortable columns, page size,
 * column show/hide, density, sticky header, row selection with bulk actions,
 * row actions, Excel/CSV export of all matching rows, skeleton / empty / error
 * states, a card layout on small screens, and per-table preferences remembered
 * in this browser (hidden columns, density, page size). `onLoad(response)` gets
 * each loaded response, e.g. for totals the endpoint sends next to the page.
 *
 * Local mode: pass `rows` (an array the page already has — a report, a small
 * settings list) instead of `endpoint`; search, sort, paging, selection and a
 * CSV export then work on those rows in the browser. `loading` shows the
 * skeleton while the page is still fetching them. A column can give
 * `value(row)` for what is searched, sorted and exported when the cell
 * renders something else. `minimal` drops the toolbar (and the pager while
 * everything fits one page) for small tables inside a record, e.g. its lines.
 *
 * `defaultDense` starts on the compact rows (wide grids such as the cash flow).
 *
 * `rowClassName(row)` adds classes to a row (e.g. a tint for overdue rows). A
 * column's `footer` (a node, or `(rows) => node` over every matching row in
 * local mode / the loaded page in server mode) fills a totals row under it.
 * `rowDetail(row)` returns a node shown full width under that row (and under
 * its mobile card), e.g. a call's transcript; return null for no detail.
 *
 * Date filter: every date column (`type: 'date'`, or a key such as
 * invoice_date / due_date / created_at) gets a date-range filter in the
 * toolbar with presets (today, last 7 days, this / last month, this / last
 * year) and a custom range. Local mode filters the rows; server mode sends
 * `date_field`, `date_from`, `date_to` (YYYY-MM-DD, inclusive), which
 * listing.py applies to a sortable field, so only sortable date columns
 * are offered there. `dateFilter={false}` turns it off.
 *
 * Defaults (Engin: "yeniden eskiye sıralı olmalı ... bu yıl seçili olmalı"):
 * a table with a date column opens newest first on that column (the page's
 * defaultSort column when it is a date, else the first date column), and
 * its date filter starts on "this year". A table whose only date is the
 * record's created_at / updated_at (customers, products...) starts on all
 * dates, so older master data is not hidden, and so does one whose main
 * date is an end / expiry / due / hire date; `defaultDatePreset` sets the
 * start explicitly ('thisYear', or null for all dates). `minimal` tables
 * have no toolbar, so they are never date-filtered.
 *
 * columns: [{ key, header, render?(row), sortable?, sortKey?, align?: 'left'|'right'|'center',
 *             width?, hideable? (default true), defaultHidden?, export? (default true), exportKey?,
 *             className?, value?(row), footer?: node | (rows) => node, type?: 'date',
 *             exportMap?, exportDecimals?, exportAlso? }]
 *
 * Export like the screen (F40): `exportMap` ({ draft: 'Taslak', ... }) turns a
 * stored value into its label and `exportDecimals` rounds numbers (2 for
 * money) in the file; `exportAlso` ([{ key, header, exportMap?, exportDecimals? }])
 * adds file-only columns right after this one, e.g. the currency of an amount.
 * In local mode `exportValue(row)` gives the file's text for a cell whose
 * `value` must stay sortable (e.g. a local date over an ISO timestamp).
 */

const PAGE_SIZES = [10, 25, 50, 100];

const DATE_KEY = /(^date$|_date$|^date_|_at$)/;
const isDateColumn = (col) => col.type === 'date' || (col.type === undefined && DATE_KEY.test(col.sortKey || col.key));
const RECORD_STAMPS = ['created_at', 'updated_at'];
// Dates that point at the future or at the start of a long-lived record
// (expiry, due, end, hire...): "this year" on them hid next year's expiries,
// last year's overdue instalments and staff hired earlier, so such tables
// start on all dates.
const OPEN_ENDED_DATE = /(^|_)(end|ends|expiry|expires|expiration|due|valid|validity|renewal|maturity|deadline|hire|hired|join|joined|birth)(_|$)/;
const startsOnAllDates = (key) => RECORD_STAMPS.includes(key) || OPEN_ENDED_DATE.test(key || '');
// Page tests turn the "this year" start off (setupTests.js sets this global)
// so their fixed fixture dates do not depend on the year the suite runs in.
const startsThisYear = () => globalThis.RUNVYA_TABLE_THIS_YEAR !== false;

// the stored value of a date column as a local calendar day (YYYY-MM-DD)
const dayOf = (col, row) => {
  const key = col.sortKey || col.key;
  const raw = key.split('.').reduce((v, part) => (v == null ? v : v[part]), row);
  if (!raw) return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = raw instanceof Date ? raw : parseStoredDate(raw);
  return d && !Number.isNaN(d.getTime()) ? toISODate(d) : '';
};

const DATE_PRESETS = ['today', 'last7', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear'];
const presetRange = (preset) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const day = (d) => toISODate(d);
  switch (preset) {
    case 'today': return { from: day(now), to: day(now) };
    case 'last7': return { from: day(new Date(y, m, now.getDate() - 6)), to: day(now) };
    case 'thisMonth': return { from: day(new Date(y, m, 1)), to: day(new Date(y, m + 1, 0)) };
    case 'lastMonth': return { from: day(new Date(y, m - 1, 1)), to: day(new Date(y, m, 0)) };
    case 'thisYear': return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'lastYear': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    default: return { from: '', to: '' };
  }
};

const plainValue = (col, row) => {
  if (col.value) return col.value(row);
  const key = col.exportKey || col.sortKey || col.key;
  return key.split('.').reduce((v, part) => (v == null ? v : v[part]), row);
};

// the columns of an export file: each exported column, then its exportAlso ones
const exportColumnsOf = (cols) => cols
  .filter((c) => c.export !== false)
  .flatMap((c) => [c, ...(c.exportAlso || [])]);

// a cell of the local CSV: like the screen (value / exportMap / exportDecimals)
const exportValue = (col, row) => {
  // a file-only text, e.g. a local date for a column that sorts on the ISO value
  if (col.exportValue) return col.exportValue(row);
  const v = plainValue(col, row);
  if (col.value) return v;
  if (col.exportMap && v != null && Object.prototype.hasOwnProperty.call(col.exportMap, String(v))) return col.exportMap[String(v)];
  if (col.exportDecimals !== undefined && typeof v === 'number') return Number(v.toFixed(col.exportDecimals));
  return v;
};

const compareValues = (a, b) => {
  const empty = (v) => v === null || v === undefined || v === '';
  if (empty(a) && empty(b)) return 0;
  if (empty(a)) return -1;
  if (empty(b)) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const readPrefs = (id) => {
  if (!id) return {};
  try {
    return JSON.parse(window.localStorage.getItem(`datatable:${id}`) || '{}') || {};
  } catch (e) {
    return {};
  }
};

const writePrefs = (id, prefs) => {
  if (!id) return;
  try {
    window.localStorage.setItem(`datatable:${id}`, JSON.stringify(prefs));
  } catch (e) {
    // storage may be unavailable (private mode); preferences are optional
  }
};

const alignClass = (align) => (align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left');

const useOutsideClose = (open, setOpen) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, setOpen]);
  return ref;
};

const pageWindow = (page, pages) => {
  const start = Math.max(1, Math.min(page - 2, pages - 4));
  const end = Math.min(pages, start + 4);
  const out = [];
  for (let p = start; p <= end; p += 1) out.push(p);
  return out;
};

/** Text folded for the local search: Turkish dotted / dotless i (U+0130,
 *  U+0131) and i / I are one letter (F35: "istanbul" did not find the
 *  capital-dotted spelling), like the backend's listing.search_pattern. */
const TR_I = new RegExp(`[iI${String.fromCharCode(0x130, 0x131)}]`, 'g');
export const searchFold = (text) => String(text ?? '').replace(TR_I, 'i').toLocaleLowerCase('tr');

/** Fired by the header search (detail: the text) when its hit is a list
 *  on the page already open. */
export const TABLE_SEARCH_EVENT = 'runvya:table-search';

/** The ?q= of the page URL (a link from the header search), else ''. */
const urlSearch = () => {
  try {
    return (new URLSearchParams(window.location.search).get('q') || '').trim();
  } catch (e) {
    return '';
  }
};

const DataTable = ({
  id,
  endpoint,
  columns,
  params = {},
  rowKey = 'id',
  title,
  toolbar,
  filters,
  searchPlaceholder,
  defaultSort,
  // the page's defaultSort as given, oldest first included (a statement
  // reads top-down from its opening balance; M2-24)
  keepSortOrder = false,
  defaultPageSize = 25,
  pageSizeOptions = PAGE_SIZES,
  selectable = false,
  bulkActions,
  rowActions,
  onRowClick,
  emptyText,
  emptyAction,
  exportName,
  exportable = true,
  reloadKey,
  onLoad,
  rows,
  loading = false,
  minimal = false,
  // start on the compact rows (the reader can still switch; their choice is kept)
  defaultDense = false,
  rowClassName,
  rowDetail,
  dateFilter = true,
  defaultDatePreset,
  className = '',
}) => {
  const local = Array.isArray(rows);
  const { t } = useTranslation();
  const prefs = useMemo(() => readPrefs(id), [id]);
  const [hidden, setHidden] = useState(() => new Set(
    prefs.hidden || columns.filter((c) => c.defaultHidden).map((c) => c.key),
  ));
  const [dense, setDense] = useState(prefs.dense === undefined ? Boolean(defaultDense) : Boolean(prefs.dense));
  const [pageSize, setPageSize] = useState(prefs.pageSize || defaultPageSize);
  const [page, setPage] = useState(1);
  // OFF-25: a list opened from the header search (?q=...) starts on that
  // search, over all dates
  const [urlQ] = useState(() => (minimal ? '' : urlSearch()));
  const [searchText, setSearchText] = useState(urlQ);
  const [q, setQ] = useState(urlQ);
  // newest first on the table's main date column (see the header comment)
  const [sort, setSort] = useState(() => {
    const sortableDates = columns.filter((c) => isDateColumn(c) && (local || c.sortable));
    const own = defaultSort && sortableDates.find((c) => (c.sortKey || c.key) === defaultSort.key);
    const main = own || (sortableDates.length ? sortableDates[0] : null);
    if (keepSortOrder && defaultSort) return defaultSort;
    if (main) return { key: main.sortKey || main.key, order: 'desc' };
    return defaultSort || { key: '', order: 'desc' };
  });
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(() => new Map());
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const columnsRef = useOutsideClose(columnsOpen, setColumnsOpen);
  const exportRef = useOutsideClose(exportOpen, setExportOpen);
  const request = useRef(0);
  // date-range filter over one date column
  const dateColumns = dateFilter ? columns.filter((c) => isDateColumn(c) && (Array.isArray(rows) || c.sortable)) : [];
  const [dateField, setDateField] = useState(() => (dateColumns.some((c) => (c.sortKey || c.key) === sort.key) ? sort.key : ''));
  const [dateRange, setDateRange] = useState(() => {
    const none = { preset: '', from: '', to: '' };
    if (minimal || urlQ || !dateColumns.length || defaultDatePreset === null || !startsThisYear()) return none;
    const main = dateColumns.find((c) => (c.sortKey || c.key) === sort.key) || dateColumns[0];
    const preset = defaultDatePreset || (startsOnAllDates(main.sortKey || main.key) ? '' : 'thisYear');
    return preset ? { preset, ...presetRange(preset) } : none;
  });
  const [dateOpen, setDateOpen] = useState(false);
  const dateRef = useOutsideClose(dateOpen, setDateOpen);
  const dateCol = dateColumns.find((c) => (c.sortKey || c.key) === dateField) || dateColumns[0];
  const dateActive = Boolean(dateCol && (dateRange.from || dateRange.to));
  const dateQuery = dateActive
    ? { date_field: dateCol.sortKey || dateCol.key, ...(dateRange.from ? { date_from: dateRange.from } : {}), ...(dateRange.to ? { date_to: dateRange.to } : {}) }
    : {};
  const paramsKey = JSON.stringify({ ...(params || {}), ...dateQuery });

  useEffect(() => {
    writePrefs(id, { hidden: [...hidden], dense, pageSize });
  }, [id, hidden, dense, pageSize]);

  // a header-search hit for the page already open (same route, no remount)
  useEffect(() => {
    if (minimal) return undefined;
    const onSearch = (e) => setSearchText(String(e.detail || ''));
    window.addEventListener(TABLE_SEARCH_EVENT, onSearch);
    return () => window.removeEventListener(TABLE_SEARCH_EVENT, onSearch);
  }, [minimal]);

  // debounce the search box
  useEffect(() => {
    const timer = setTimeout(() => { setQ(searchText.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // a filter change starts again from the first page
  useEffect(() => { setPage(1); }, [paramsKey]);

  // latest onLoad without re-running the fetch when the parent re-renders
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  const visibleColumns = columns.filter((c) => !hidden.has(c.key));
  const keyOf = useCallback((row) => row[rowKey] ?? row.id ?? row._id, [rowKey]);

  // local mode: search, sort and page the rows the page passed in
  const localRows = useMemo(() => {
    if (!local) return [];
    const words = searchFold(q).split(/\s+/).filter(Boolean);
    let out = rows;
    if (dateActive) {
      out = out.filter((row) => {
        const d = dayOf(dateCol, row);
        // a row with no date in that column stays in (it would vanish otherwise)
        return !d || ((!dateRange.from || d >= dateRange.from) && (!dateRange.to || d <= dateRange.to));
      });
    }
    if (words.length) {
      out = out.filter((row) => {
        const text = searchFold(columns.map((c) => plainValue(c, row)).filter((v) => v != null).join(' '));
        return words.every((w) => text.includes(w));
      });
    }
    if (sort.key) {
      const col = columns.find((c) => (c.sortKey || c.key) === sort.key) || { key: sort.key };
      const dir = sort.order === 'desc' ? -1 : 1;
      out = [...out].sort((a, b) => dir * compareValues(plainValue(col, a), plainValue(col, b)));
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, rows, q, sort, columns, paramsKey]);

  useEffect(() => {
    if (!local) return;
    const pages = Math.max(1, Math.ceil(localRows.length / pageSize));
    const current = Math.min(page, pages);
    setData({ items: localRows.slice((current - 1) * pageSize, current * pageSize), total: localRows.length, pages, page: current });
    setStatus(loading ? 'loading' : 'ready');
  }, [local, localRows, page, pageSize, loading]);

  const load = useCallback(async () => {
    if (local) return;
    const ticket = request.current + 1;
    request.current = ticket;
    setRefreshing(true);
    try {
      const query = { ...JSON.parse(paramsKey), page, page_size: pageSize };
      if (q) query.q = q;
      if (sort.key) { query.sort = sort.key; query.order = sort.order; }
      const { data: res } = await axios.get(endpoint, { params: query });
      if (request.current !== ticket) return;
      const items = Array.isArray(res) ? res : (res.items || []);
      setData({
        items,
        total: Array.isArray(res) ? res.length : (res.total ?? items.length),
        pages: Array.isArray(res) ? 1 : (res.pages || 1),
        page: Array.isArray(res) ? 1 : (res.page || page),
      });
      setStatus('ready');
      if (onLoadRef.current) onLoadRef.current(res);
    } catch (e) {
      if (request.current === ticket) setStatus('error');
    } finally {
      if (request.current === ticket) setRefreshing(false);
    }
  }, [local, endpoint, paramsKey, page, pageSize, q, sort]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const toggleSort = (col) => {
    if (!col.sortable) return;
    const key = col.sortKey || col.key;
    setSort((s) => (s.key === key ? { key, order: s.order === 'asc' ? 'desc' : 'asc' } : { key, order: 'asc' }));
    setPage(1);
  };

  const toggleColumn = (key) => setHidden((h) => {
    const next = new Set(h);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const pageKeys = data.items.map(keyOf);
  const allOnPage = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const someOnPage = pageKeys.some((k) => selected.has(k));
  const toggleRow = (row) => setSelected((m) => {
    const next = new Map(m);
    const k = keyOf(row);
    if (next.has(k)) next.delete(k); else next.set(k, row);
    return next;
  });
  const togglePage = () => setSelected((m) => {
    const next = new Map(m);
    if (allOnPage) data.items.forEach((r) => next.delete(keyOf(r)));
    else data.items.forEach((r) => next.set(keyOf(r), r));
    return next;
  });
  const clearSelection = () => setSelected(new Map());

  const downloadBlob = (blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  // local mode: every matching row as a CSV Excel opens with Turkish letters
  const exportLocal = () => {
    setExportOpen(false);
    const cols = exportColumnsOf(visibleColumns);
    // utils/csv: ';', decimal comma and a BOM, like the server's exports (S2-31)
    const lines = [cols.map((c) => (typeof c.header === 'string' ? c.header : c.key))];
    localRows.forEach((row) => lines.push(cols.map((c) => exportValue(c, row))));
    downloadBlob(new Blob([csvText(lines)], { type: 'text/csv;charset=utf-8' }), `${exportName || 'liste'}.csv`);
  };

  const doExport = async (format) => {
    if (local) { exportLocal(); return; }
    setExportOpen(false);
    setExporting(true);
    try {
      const cols = exportColumnsOf(visibleColumns).map((c) => ({
        key: c.exportKey || c.key,
        label: typeof c.header === 'string' ? c.header : c.key,
        ...(c.exportMap ? { map: c.exportMap } : {}),
        ...(c.exportDecimals !== undefined ? { decimals: c.exportDecimals } : {}),
      }));
      const query = { ...JSON.parse(paramsKey), export: format, columns: JSON.stringify(cols) };
      if (q) query.q = q;
      if (sort.key) { query.sort = sort.key; query.order = sort.order; }
      const res = await axios.get(endpoint, { params: query, responseType: 'blob' });
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const name = match ? match[1] : `${exportName || 'liste'}.${format}`;
      downloadBlob(res.data, name);
    } catch (e) {
      // the global axios interceptor shows the error toast
    } finally {
      setExporting(false);
    }
  };

  const from = data.total === 0 ? 0 : (data.page - 1) * pageSize + 1;
  const to = Math.min(data.total, data.page * pageSize);
  const cellPad = dense ? 'px-3 py-1.5' : 'px-4 py-3';
  const showActions = Boolean(rowActions);
  const colSpan = visibleColumns.length + (selectable ? 1 : 0) + (showActions ? 1 : 0);

  const cell = (col, row) => (col.render ? col.render(row) : (row[col.key] ?? ''));
  const footerCols = visibleColumns.filter((c) => c.footer !== undefined);
  const showFooter = footerCols.length > 0 && status === 'ready' && data.items.length > 0;
  const footerCell = (col) => (typeof col.footer === 'function' ? col.footer(local ? localRows : data.items) : col.footer);

  return (
    <div className={`table-container ${className}`} data-testid={id ? `datatable-${id}` : 'datatable'}>
      {/* toolbar */}
      {!minimal && (
        <div className="flex flex-col gap-3 p-3 md:p-4 border-b border-surface-200 dark:border-surface-800">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {title && <h2 className="text-base font-semibold text-surface-900 dark:text-surface-100 md:mr-2">{title}</h2>}
            <div className="relative flex-1 min-w-0 md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" aria-hidden="true" />
              <input
                type="search"
                className="input pl-9 pr-8"
                placeholder={searchPlaceholder || t('table.search')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                aria-label={t('table.search')}
                data-testid="datatable-search"
              />
              {searchText && (
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-surface-400 hover:text-surface-600"
                  onClick={() => setSearchText('')} aria-label={t('table.clearSearch')}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {dateColumns.length > 0 && (
              <div className="relative" ref={dateRef}>
                <button type="button" className={`btn-secondary btn-sm inline-flex items-center gap-1.5 ${dateActive ? '!border-primary-400 text-primary-700 dark:text-primary-300' : ''}`}
                  onClick={() => setDateOpen((o) => !o)} aria-expanded={dateOpen} data-testid="datatable-date">
                  <CalendarDays className="w-4 h-4" aria-hidden="true" />
                  <span className="whitespace-nowrap">
                    {!dateActive ? t('table.date.button')
                      : dateRange.preset ? t(`table.date.${dateRange.preset}`)
                        : `${dateRange.from ? formatDate(dateRange.from) : '…'} – ${dateRange.to ? formatDate(dateRange.to) : '…'}`}
                  </span>
                </button>
                {dateActive && (
                  <button type="button" className="ms-1 p-1 text-surface-400 hover:text-surface-600 align-middle" aria-label={t('table.date.clear')}
                    onClick={() => setDateRange({ preset: '', from: '', to: '' })} data-testid="datatable-date-clear">
                    <X className="w-4 h-4" />
                  </button>
                )}
                {dateOpen && (
                  <div className="absolute z-30 mt-1 w-72 rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 shadow-lg p-3 space-y-3"
                    data-testid="datatable-date-panel">
                    {dateColumns.length > 1 && (
                      <label className="block text-xs text-surface-500">
                        {t('table.date.field')}
                        <select className="select mt-1" value={dateCol.sortKey || dateCol.key} onChange={(e) => setDateField(e.target.value)}
                          data-testid="datatable-date-field">
                          {dateColumns.map((c) => (
                            <option key={c.key} value={c.sortKey || c.key}>{typeof c.header === 'string' ? c.header : c.key}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    <div className="grid grid-cols-2 gap-1.5">
                      {DATE_PRESETS.map((preset) => (
                        <button key={preset} type="button" data-testid={`datatable-date-${preset}`}
                          className={`btn-sm rounded-md border text-xs py-1.5 ${dateRange.preset === preset ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800'}`}
                          onClick={() => { setDateRange({ preset, ...presetRange(preset) }); setDateOpen(false); }}>
                          {t(`table.date.${preset}`)}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-xs text-surface-500">
                        {t('table.date.from')}
                        <input type="date" className="input mt-1 !py-1" value={dateRange.from} data-testid="datatable-date-from"
                          onChange={(e) => setDateRange((r) => ({ preset: '', from: e.target.value, to: r.to }))} />
                      </label>
                      <label className="text-xs text-surface-500">
                        {t('table.date.to')}
                        <input type="date" className="input mt-1 !py-1" value={dateRange.to} data-testid="datatable-date-to"
                          onChange={(e) => setDateRange((r) => ({ preset: '', from: r.from, to: e.target.value }))} />
                      </label>
                    </div>
                    <div className="flex justify-between">
                      <button type="button" className="btn-ghost btn-sm" onClick={() => setDateRange({ preset: '', from: '', to: '' })}>
                        {t('table.date.all')}
                      </button>
                      <button type="button" className="btn-primary btn-sm" onClick={() => setDateOpen(false)}>{t('table.date.apply')}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {filters && <div className="flex flex-wrap items-center gap-2">{filters}</div>}
            <div className="flex items-center gap-1 md:ml-auto">
              {!local && (
                <button type="button" className="btn-ghost btn-icon" onClick={() => load()} title={t('table.refresh')} aria-label={t('table.refresh')}>
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              )}
              <button type="button" className={`btn-ghost btn-icon ${dense ? 'text-primary-600' : ''}`} onClick={() => setDense((d) => !d)}
                title={dense ? t('table.comfortable') : t('table.compact')} aria-label={t('table.density')} aria-pressed={dense}>
                <Rows3 className="w-4 h-4" />
              </button>
              <div className="relative" ref={columnsRef}>
                <button type="button" className="btn-ghost btn-icon" onClick={() => setColumnsOpen((o) => !o)}
                  title={t('table.columns')} aria-label={t('table.columns')} aria-expanded={columnsOpen} data-testid="datatable-columns">
                  <Columns3 className="w-4 h-4" />
                </button>
                {columnsOpen && (
                  <div className="absolute right-0 z-30 mt-1 w-56 max-h-80 overflow-auto rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 shadow-pop p-2">
                    <div className="px-2 pb-1 text-xs font-medium text-surface-500 uppercase tracking-wider">{t('table.columns')}</div>
                    {columns.filter((c) => c.hideable !== false).map((c) => (
                      <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-50 dark:hover:bg-surface-700 text-sm cursor-pointer">
                        <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggleColumn(c.key)} />
                        <span className="truncate">{c.header}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {exportable && (
                <div className="relative" ref={exportRef}>
                  <button type="button" className="btn-ghost btn-icon" onClick={() => setExportOpen((o) => !o)} disabled={exporting}
                    title={t('table.export')} aria-label={t('table.export')} aria-expanded={exportOpen} data-testid="datatable-export">
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  </button>
                  {exportOpen && (
                    <div className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 shadow-pop p-1">
                      {!local && (
                        <button type="button" className="w-full text-left px-3 py-2 rounded text-sm hover:bg-surface-50 dark:hover:bg-surface-700" onClick={() => doExport('xlsx')}>
                          {t('table.exportExcel')}
                        </button>
                      )}
                      <button type="button" className="w-full text-left px-3 py-2 rounded text-sm hover:bg-surface-50 dark:hover:bg-surface-700" onClick={() => doExport('csv')}>
                        {t('table.exportCsv')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {toolbar}
            </div>
          </div>
          {selectable && selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg bg-primary-50 dark:bg-primary-900/30 px-3 py-2 text-sm" data-testid="datatable-bulk">
              <span className="font-medium text-primary-700 dark:text-primary-300">{t('table.selected', { count: selected.size })}</span>
              {bulkActions && bulkActions([...selected.values()], clearSelection)}
              <button type="button" className="btn-ghost btn-sm ml-auto" onClick={clearSelection}>{t('table.clearSelection')}</button>
            </div>
          )}
        </div>
      )}

      {/* table (md and up) */}
      <div className={`hidden md:block overflow-x-auto max-h-[70vh] overflow-y-auto relative ${refreshing && status === 'ready' ? 'opacity-70' : ''}`}>
        <table className="data-table">
          <thead>
            <tr>
              {selectable && (
                <th className={`${cellPad} w-10 sticky top-0 z-10`}>
                  <input type="checkbox" aria-label={t('table.selectPage')} checked={allOnPage}
                    ref={(el) => { if (el) el.indeterminate = !allOnPage && someOnPage; }} onChange={togglePage} />
                </th>
              )}
              {visibleColumns.map((col) => {
                const key = col.sortKey || col.key;
                const active = sort.key === key;
                const ariaSort = active ? (sort.order === 'asc' ? 'ascending' : 'descending') : 'none';
                return (
                  <th key={col.key} className={`${cellPad} sticky top-0 z-10 ${alignClass(col.align)} ${col.className || ''}`}
                    style={col.width ? { width: col.width } : undefined} aria-sort={col.sortable ? ariaSort : undefined}>
                    {col.sortable ? (
                      <button type="button" className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-surface-900 dark:hover:text-surface-100 ${active ? 'text-primary-600 dark:text-primary-400' : ''}`}
                        onClick={() => toggleSort(col)}>
                        {col.header}
                        {active ? (sort.order === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
                          : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                      </button>
                    ) : col.header}
                  </th>
                );
              })}
              {showActions && <th className={`${cellPad} sticky top-0 z-10 text-right`}><span className="sr-only">{t('table.actions')}</span></th>}
            </tr>
          </thead>
          <tbody>
            {status === 'loading' && Array.from({ length: 6 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`sk-${i}`}>
                {Array.from({ length: colSpan }).map((__, j) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={j} className={cellPad}><div className="skeleton h-4 w-full max-w-[12rem]" /></td>
                ))}
              </tr>
            ))}
            {status === 'error' && (
              <tr><td colSpan={colSpan} className="empty-state">
                <p className="mb-3">{t('table.error')}</p>
                <button type="button" className="btn-secondary btn-sm" onClick={() => load()}>{t('table.retry')}</button>
              </td></tr>
            )}
            {status === 'ready' && data.items.length === 0 && (
              <tr><td colSpan={colSpan} className="empty-state">
                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-40" aria-hidden="true" />
                <p>{q ? t('table.noMatch') : (emptyText || t('table.empty'))}</p>
                {!q && emptyAction && <div className="mt-3">{emptyAction}</div>}
              </td></tr>
            )}
            {status === 'ready' && data.items.map((row) => {
              const k = keyOf(row);
              const isSel = selected.has(k);
              const detail = rowDetail ? rowDetail(row) : null;
              return (
                <React.Fragment key={k}>
                <tr className={`${onRowClick ? 'cursor-pointer' : ''} ${isSel ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''} ${rowClassName ? rowClassName(row) || '' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined} data-testid={`datatable-row-${k}`}>
                  {selectable && (
                    <td className={cellPad} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleRow(row)} aria-label={t('table.selectRow')} />
                    </td>
                  )}
                  {visibleColumns.map((col) => (
                    <td key={col.key} className={`${cellPad} ${alignClass(col.align)} ${col.className || ''}`}>{cell(col, row)}</td>
                  ))}
                  {showActions && (
                    <td className={`${cellPad} text-right whitespace-nowrap`} onClick={(e) => e.stopPropagation()}>{rowActions(row)}</td>
                  )}
                </tr>
                {detail && (
                  <tr className={rowClassName ? rowClassName(row) || '' : ''} data-testid={`datatable-detail-${k}`}>
                    <td colSpan={colSpan} className="px-4 pb-3 pt-0">{detail}</td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
          {showFooter && (
            <tfoot>
              <tr className="font-semibold bg-surface-50 dark:bg-surface-800/60" data-testid="datatable-footer">
                {selectable && <td className={cellPad} />}
                {visibleColumns.map((col) => (
                  <td key={col.key} className={`${cellPad} ${alignClass(col.align)} ${col.className || ''}`}>{col.footer !== undefined ? footerCell(col) : ''}</td>
                ))}
                {showActions && <td className={cellPad} />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* cards (small screens) */}
      <div className="md:hidden divide-y divide-surface-100 dark:divide-surface-800">
        {status === 'loading' && <div className="p-4 space-y-2"><div className="skeleton h-4 w-2/3" /><div className="skeleton h-4 w-1/2" /></div>}
        {status === 'error' && (
          <div className="empty-state"><p className="mb-3">{t('table.error')}</p>
            <button type="button" className="btn-secondary btn-sm" onClick={() => load()}>{t('table.retry')}</button></div>
        )}
        {status === 'ready' && data.items.length === 0 && <div className="empty-state">{q ? t('table.noMatch') : (emptyText || t('table.empty'))}</div>}
        {status === 'ready' && data.items.map((row) => {
          const k = keyOf(row);
          const [first, ...rest] = visibleColumns;
          return (
            <div key={k} className={`p-4 ${onRowClick ? 'cursor-pointer active:bg-surface-50' : ''} ${rowClassName ? rowClassName(row) || '' : ''}`} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              <div className="flex items-start gap-3">
                {selectable && <input type="checkbox" className="mt-1" checked={selected.has(k)} onClick={(e) => e.stopPropagation()} onChange={() => toggleRow(row)} aria-label={t('table.selectRow')} />}
                <div className="flex-1 min-w-0">
                  {first && <div className="font-medium text-surface-900 dark:text-surface-100 truncate">{cell(first, row)}</div>}
                  <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    {rest.map((col) => (
                      <React.Fragment key={col.key}>
                        <dt className="text-surface-500">{col.header}</dt>
                        <dd className="text-surface-800 dark:text-surface-200 text-right truncate">{cell(col, row)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              </div>
              {showActions && <div className="mt-2 flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>{rowActions(row)}</div>}
              {rowDetail && rowDetail(row) && <div className="mt-2">{rowDetail(row)}</div>}
            </div>
          );
        })}
        {showFooter && (
          <dl className="p-4 grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-semibold bg-surface-50 dark:bg-surface-800/60">
            {footerCols.map((col) => (
              <React.Fragment key={col.key}>
                <dt className="text-surface-500">{col.header}</dt>
                <dd className="text-surface-800 dark:text-surface-200 text-right">{footerCell(col)}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
      </div>

      {/* footer */}
      {!(minimal && data.pages <= 1) && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 md:px-4 py-3 text-sm text-surface-600 dark:text-surface-400">
          <div className="flex items-center gap-3">
            <span data-testid="datatable-range">{t('table.range', { from, to, total: data.total })}</span>
            <label className="flex items-center gap-2">
              <span className="hidden sm:inline">{t('table.perPage')}</span>
              <select className="select !w-auto !py-1" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                aria-label={t('table.perPage')}>
                {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <nav className="flex items-center gap-1" aria-label={t('table.pagination')}>
            <button type="button" className="btn-ghost btn-icon" disabled={data.page <= 1} onClick={() => setPage(1)} aria-label={t('table.first')}><ChevronsLeft className="w-4 h-4" /></button>
            <button type="button" className="btn-ghost btn-icon" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)} aria-label={t('table.previous')}><ChevronLeft className="w-4 h-4" /></button>
            {pageWindow(data.page, data.pages).map((p) => (
              <button key={p} type="button" onClick={() => setPage(p)} aria-current={p === data.page ? 'page' : undefined}
                className={`min-w-[2rem] h-8 px-2 rounded-lg text-sm ${p === data.page ? 'bg-primary-600 text-white' : 'hover:bg-surface-100 dark:hover:bg-surface-800'}`}>
                {p}
              </button>
            ))}
            <button type="button" className="btn-ghost btn-icon" disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)} aria-label={t('table.next')}><ChevronRight className="w-4 h-4" /></button>
            <button type="button" className="btn-ghost btn-icon" disabled={data.page >= data.pages} onClick={() => setPage(data.pages)} aria-label={t('table.last')}><ChevronsRight className="w-4 h-4" /></button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default DataTable;
